import { useState, useEffect, useCallback } from 'react';
import { search, searchSuggest } from '@/lib/api';
import { useDebounce } from './useDebounce';

export function useSearch(initialQuery = '') {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState({ types: {}, categories: {} });
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    types: null,
    category: null,
    sort: 'relevance'
  });

  const debouncedQuery = useDebounce(query, 300);

  const executeSearch = useCallback(async (searchQuery, searchPage = 1, searchFilters = filters) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults([]);
      setTotal(0);
      setFacets({ types: {}, categories: {} });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = {
        q: searchQuery,
        page: searchPage,
        limit: 20,
        sort: searchFilters.sort
      };

      if (searchFilters.types) {
        params.types = searchFilters.types;
      }
      if (searchFilters.category) {
        params.category = searchFilters.category;
      }

      const response = await search(params);
      
      if (searchPage === 1) {
        setResults(response.data.results);
      } else {
        setResults(prev => [...prev, ...response.data.results]);
      }
      
      setTotal(response.data.total);
      setFacets(response.data.facets);
      setPage(searchPage);
    } catch (err) {
      setError(err.message || 'Search failed');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Auto-search when debounced query changes
  useEffect(() => {
    if (debouncedQuery) {
      executeSearch(debouncedQuery, 1, filters);
    }
  }, [debouncedQuery, filters, executeSearch]);

  const loadMore = useCallback(() => {
    if (!isLoading && results.length < total) {
      executeSearch(query, page + 1, filters);
    }
  }, [isLoading, results.length, total, query, page, filters, executeSearch]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setTotal(0);
    setFacets({ types: {}, categories: {} });
    setPage(1);
    setError(null);
  }, []);

  return {
    query,
    setQuery,
    results,
    facets,
    total,
    isLoading,
    error,
    page,
    filters,
    updateFilters,
    loadMore,
    clearSearch,
    hasMore: results.length < total
  };
}

export function useSuggestions(query, enabled = true) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const debouncedQuery = useDebounce(query, 150);

  useEffect(() => {
    if (!enabled || !debouncedQuery || debouncedQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      setIsLoading(true);
      try {
        const response = await searchSuggest(debouncedQuery, 6);
        setSuggestions(response.data.suggestions);
      } catch (err) {
        console.error('Suggestions error:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery, enabled]);

  return { suggestions, isLoading };
}
